/**
 * Mobile Menu Component
 * Handles slide-in navigation with multi-level menu support.
 */

class MobileMenuManager {
  constructor() {
    this.menu = null;
    this.overlay = null;
    /** @type {HTMLElement[]} */
    this.triggers = [];
    this.closeButton = null;
    this.backButton = null;
    this.mainPanel = null;
    this.submenuContainer = null;
    this.activeSubmenu = null;
    this.activeSubcategory = null;
    this.focusedElementBeforeOpen = null;
    this.previousScrollY = 0;
    this.focusTrapHandler = null;
    this.init();
  }

  init() {
    this.menu = document.querySelector('[data-mobile-menu]');
    this.overlay = document.querySelector('[data-mobile-menu-overlay]');
    this.triggers = [...document.querySelectorAll('[data-mobile-menu-trigger]')];
    this.closeButton = document.querySelector('[data-mobile-menu-close]');
    this.backButton = document.querySelector('[data-menu-back]');
    this.mainPanel = document.querySelector('[data-menu-panel="main"]');
    this.submenuContainer = document.querySelector('[data-submenu-container]');

    if (!this.menu || !this.overlay) return;

    this.setupEventListeners();
    this.setupKeyboard();
    this.setInitialInertState();
  }

  setInitialInertState() {
    if (!this.submenuContainer) return;

    const allWrappers = this.submenuContainer.querySelectorAll('.mobile-menu-block-wrapper');
    for (const wrapper of allWrappers) {
      wrapper.inert = true;

      const submenuBlock = wrapper.querySelector('.mobile-menu-block-submenu');
      if (submenuBlock) {
        submenuBlock.inert = true;
        submenuBlock.setAttribute('aria-hidden', 'true');
      }
    }
  }

  setupEventListeners() {
    for (const trigger of this.triggers) {
      trigger.addEventListener('click', () => this.open());
    }

    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', () => this.close());

    this.backButton?.addEventListener('click', () => this.navigateBack());

    document.addEventListener('mobile-menu:toggle', () => {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    });

    document.addEventListener('mobile-menu:close', () => {
      if (this.isOpen()) {
        this.close();
      }
    });

    document.addEventListener('click', (e) => {
      const menuItem = e.target.closest('[data-mobile-menu-item]');
      if (menuItem) {
        const blockId = menuItem.dataset.mobileMenuItem;
        const menuType = menuItem.dataset.menuType;
        this.openSubmenu(blockId, menuType);
      }
    });

    document.addEventListener('click', (e) => {
      const categoryItem = e.target.closest('[data-category-trigger]');
      if (categoryItem) {
        const categoryId = categoryItem.dataset.categoryId;
        const blockId = categoryItem.dataset.categoryBlockId;
        const submenuAlreadyActive = this.activeSubmenu?.dataset.submenuPanel === blockId;

        if (blockId && !submenuAlreadyActive) {
          this.openSubmenu(blockId, 'megamenu', { skipWrapperTransition: true });
        }

        this.openSubcategory(categoryId);
      }
    });

    document.addEventListener('click', (e) => {
      const submenuBack = e.target.closest('[data-submenu-back]');
      if (submenuBack) {
        e.preventDefault();
        e.stopPropagation();
        if (this.activeSubmenu) {
          this.closeSubmenu();
        }
      }
    });

    document.addEventListener('click', (e) => {
      const submenuClose = e.target.closest('[data-submenu-close]');
      if (submenuClose) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });

    document.addEventListener('click', (e) => {
      const level3Back = e.target.closest('[data-level3-back]');
      if (level3Back) {
        e.preventDefault();
        e.stopPropagation();
        if (this.activeSubcategory) {
          this.closeSubcategory();
        }
      }
    });

    document.addEventListener('click', (e) => {
      const level3Close = e.target.closest('[data-level3-close]');
      if (level3Close) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });
  }

  setupKeyboard() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        if (this.activeSubcategory) {
          this.navigateBack();
        } else if (this.activeSubmenu) {
          this.navigateBack();
        } else {
          this.close();
        }
      }
    });
  }

  open() {
    this.focusedElementBeforeOpen = document.activeElement;

    const scrollY = window.scrollY;
    this.previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      this.menu.classList.add('is-active');
      this.overlay.classList.add('is-active');

      for (const trigger of this.triggers) {
        trigger.setAttribute('aria-expanded', 'true');
      }

      const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
      menuToggle?.setAttribute('aria-expanded', 'true');

      this.menu.setAttribute('aria-hidden', 'false');
      this.menu.removeAttribute('inert');

      document.dispatchEvent(new CustomEvent('mobile-menu:opened'));
      this.focusFirstElement();
      this.setupFocusTrap();
    });
  }

  close() {
    this.menu.classList.remove('is-active');
    this.overlay.classList.remove('is-active');

    for (const trigger of this.triggers) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    menuToggle?.setAttribute('aria-expanded', 'false');

    this.menu.setAttribute('aria-hidden', 'true');
    this.menu.inert = true;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.previousScrollY, behavior: 'instant' });

    if (this.activeSubmenu) {
      this.closeSubmenu();
    }

    this.removeFocusTrap();
    document.dispatchEvent(new CustomEvent('mobile-menu:closed'));

    if (this.focusedElementBeforeOpen) {
      this.focusedElementBeforeOpen.focus();
      this.focusedElementBeforeOpen = null;
    }
  }

  openSubmenu(blockId, menuType, { skipWrapperTransition = false } = {}) {
    let submenuPanel = document.querySelector(`[data-submenu-panel="${blockId}"]`);
    if (!submenuPanel) {
      submenuPanel = document.querySelector(`[data-submenu-panel^="${blockId}"]`);
    }

    if (!submenuPanel) return;

    const allWrappers = this.submenuContainer.querySelectorAll('.mobile-menu-block-wrapper');
    for (const wrapper of allWrappers) {
      wrapper.classList.remove('is-active');
      wrapper.inert = true;

      const submenuBlock = wrapper.querySelector('.mobile-menu-block-submenu');
      if (submenuBlock) {
        submenuBlock.inert = true;
        submenuBlock.setAttribute('aria-hidden', 'true');
      }
    }

    const panelId = submenuPanel.dataset.submenuPanel;
    const wrapper = Array.from(allWrappers).find(w =>
      w.dataset.blockId && w.dataset.blockId.startsWith(panelId)
    );

    if (!wrapper) return;

    if (skipWrapperTransition) {
      // Jumping straight to a level 3 panel (from an inline-expanded item) - show
      // the wrapper instantly instead of fading it in, so the level 2 content it
      // contains never becomes visible while the level 3 panel slides on top of it.
      wrapper.style.transition = 'none';
    }

    wrapper.classList.add('is-active');
    wrapper.inert = false;

    if (skipWrapperTransition) {
      void wrapper.offsetHeight;
      requestAnimationFrame(() => {
        wrapper.style.transition = '';
      });
    }

    const submenuBlock = wrapper.querySelector('.mobile-menu-block-submenu');
    if (submenuBlock) {
      submenuBlock.inert = false;
      submenuBlock.setAttribute('aria-hidden', 'false');
    }

    this.activeSubmenu = submenuPanel;
    this.activeSubcategory = null;
    this.menu.classList.add('has-submenu-active');
    this.menu.classList.remove('has-subcategory-active');

    if (this.mainPanel) {
      this.mainPanel.inert = true;
    }

    window.mobileBottomNav?.setInert(true);

    if (this.backButton) {
      this.backButton.style.opacity = '1';
      this.backButton.style.pointerEvents = 'auto';
    }

    this.updateCategoryAriaStates(submenuPanel);

    const firstFocusable = submenuPanel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 300);
    }
  }

  openSubcategory(categoryId) {
    const activeWrapper = this.submenuContainer.querySelector('.mobile-menu-block-wrapper.is-active');
    if (!activeWrapper) return;

    const subcategoryPanel = activeWrapper.querySelector(`[data-subcategory-panel="${categoryId}"]`);
    if (!subcategoryPanel) return;

    const allLevel3Panels = activeWrapper.querySelectorAll('.digi-mobile-menu__level3-panel');
    for (const panel of allLevel3Panels) {
      panel.classList.remove('is-active');
      panel.inert = true;
    }

    subcategoryPanel.classList.add('is-active');
    subcategoryPanel.inert = false;

    this.activeSubcategory = subcategoryPanel;
    this.menu.classList.add('has-subcategory-active');

    const submenuContent = this.activeSubmenu.querySelector('.digi-mobile-menu__submenu-content');
    if (submenuContent) {
      submenuContent.inert = true;
    }
    const submenuHeader = this.activeSubmenu.querySelector('.digi-mobile-menu__submenu-header');
    if (submenuHeader) {
      submenuHeader.inert = true;
    }

    const triggerButton = this.activeSubmenu.querySelector(`[data-category-id="${categoryId}"]`);
    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', 'true');
    }

    const firstFocusable = subcategoryPanel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 300);
    }
  }

  closeSubcategory() {
    if (this.activeSubmenu?.dataset.inlineSource === 'true') {
      // Keep the level 3 panel visually on top while the whole submenu slides
      // away, instead of snapping back to reveal the level 2 panel underneath.
      this.closeSubmenu({ skipLevel3Reset: true });
      return;
    }

    this.menu.classList.remove('has-subcategory-active');

    if (this.activeSubcategory) {
      this.activeSubcategory.classList.remove('is-active');
      this.activeSubcategory.inert = true;
    }

    if (this.activeSubmenu) {
      const submenuContent = this.activeSubmenu.querySelector('.digi-mobile-menu__submenu-content');
      if (submenuContent) {
        submenuContent.inert = false;
      }
      const submenuHeader = this.activeSubmenu.querySelector('.digi-mobile-menu__submenu-header');
      if (submenuHeader) {
        submenuHeader.inert = false;
      }
    }

    this.updateCategoryAriaStates(this.activeSubmenu);
    this.activeSubcategory = null;
  }

  closeSubmenu({ skipLevel3Reset = false } = {}) {
    this.menu.classList.remove('has-submenu-active');
    this.menu.classList.remove('has-subcategory-active');

    if (this.activeSubmenu) {
      this.activeSubmenu.classList.remove('is-active');
    }

    const allWrappers = this.submenuContainer.querySelectorAll('.mobile-menu-block-wrapper');
    for (const wrapper of allWrappers) {
      wrapper.classList.remove('is-active');
      wrapper.inert = true;

      const submenuBlock = wrapper.querySelector('.mobile-menu-block-submenu');
      if (submenuBlock) {
        submenuBlock.inert = true;
        submenuBlock.setAttribute('aria-hidden', 'true');
      }
    }

    if (!skipLevel3Reset) {
      const allLevel3Panels = document.querySelectorAll('.digi-mobile-menu__level3-panel');
      for (const panel of allLevel3Panels) {
        panel.classList.remove('is-active');
        panel.inert = true;
      }
    }

    this.activeSubmenu = null;
    this.activeSubcategory = null;

    if (this.mainPanel) {
      this.mainPanel.inert = false;
    }

    window.mobileBottomNav?.setInert(false);

    if (this.backButton) {
      this.backButton.style.opacity = '0';
      this.backButton.style.pointerEvents = 'none';
    }
  }

  navigateBack() {
    if (this.activeSubcategory) {
      const categoryId = this.activeSubcategory.dataset.subcategoryPanel;
      const wasInlineSource = this.activeSubmenu?.dataset.inlineSource === 'true';
      this.closeSubcategory();

      const focusTarget = wasInlineSource
        ? this.mainPanel?.querySelector(`[data-category-id="${categoryId}"]`)
        : this.activeSubmenu?.querySelector(`[data-category-id="${categoryId}"]`);
      if (focusTarget) {
        setTimeout(() => focusTarget.focus(), 300);
      }
    } else if (this.activeSubmenu) {
      const panelId = this.activeSubmenu.dataset.submenuPanel;
      this.closeSubmenu();

      const focusTarget = this.mainPanel?.querySelector(`[data-mobile-menu-item="${panelId}"]`);
      if (focusTarget) {
        setTimeout(() => focusTarget.focus(), 300);
      } else {
        const firstButton = this.mainPanel?.querySelector('button');
        if (firstButton) {
          setTimeout(() => firstButton.focus(), 300);
        }
      }
    }
  }

  updateCategoryAriaStates(panel) {
    if (!panel) return;
    const triggers = panel.querySelectorAll('[data-category-trigger]');
    for (const trigger of triggers) {
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  isOpen() {
    return this.menu.classList.contains('is-active');
  }

  focusFirstElement() {
    const selectors = 'button:not([disabled]), a[href]';
    const candidates = this.menu.querySelectorAll(selectors);

    for (const el of candidates) {
      if (el.closest('[inert]')) continue;
      setTimeout(() => el.focus(), 100);
      break;
    }
  }

  getFocusableElements() {
    const selectors = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    let container = this.menu;

    if (this.activeSubcategory) {
      container = this.activeSubcategory;
    } else if (this.activeSubmenu) {
      const activeWrapper = this.submenuContainer.querySelector('.mobile-menu-block-wrapper.is-active');
      container = activeWrapper || this.menu;
    }

    const elements = Array.from(container.querySelectorAll(selectors));

    return elements.filter(el => {
      if (el.closest('[inert]')) return false;

      const style = window.getComputedStyle(el);
      return style.display !== 'none' &&
             style.visibility !== 'hidden' &&
             el.offsetParent !== null;
    });
  }

  setupFocusTrap() {
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (!this.isOpen()) return;

      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) return;

      const isInSubmenu = this.activeSubmenu || this.activeSubcategory;
      let trapElements = focusableElements;

      if (!isInSubmenu) {
        const bottomNavFocusable = window.mobileBottomNav?.getFocusableElements() ?? [];
        const combined = [...focusableElements, ...bottomNavFocusable.filter(el => !focusableElements.includes(el))];
        trapElements = [...new Set(combined)];
      }

      if (trapElements.length === 0) return;

      const firstElement = trapElements[0];
      const lastElement = trapElements[trapElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);
  }

  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MobileMenuManager();
  });
} else {
  new MobileMenuManager();
}
